import { useMemo } from 'react';
import { cn } from '@/lib/utils';

/**
 * Avatar com iniciais coloridas (E13.F9). Cor derivada de hash leve do // ux-neutrality:allow doc-comment
 * texto da fonte (e-mail ou nome do escritório) para que o mesmo usuário // ux-neutrality:allow doc-comment
 * sempre veja a mesma cor. Sem upload de imagem (rota de backend ainda não existe). // ux-neutrality:allow doc-comment
 */
export interface AvatarInitialsProps {
  /** Fonte das iniciais — primeiro caractere do prefixo antes do `@` ou separadores. */
  source: string;
  size?: number;
  className?: string;
  'data-testid'?: string;
}

const PALETTE: readonly string[] = [
  'bg-accent-200 text-accent-800',
  'bg-success-50 text-success-700',
  'bg-warning-50 text-warning-700',
  'bg-danger-50 text-danger-700',
  'bg-surface-2 text-fg',
];

const FALLBACK_PALETTE = PALETTE[0] ?? 'bg-surface-2 text-fg';

function hashPalette(input: string): string {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 31 + input.charCodeAt(i)) | 0;
  }
  const idx = PALETTE.length === 0 ? 0 : Math.abs(hash) % PALETTE.length;
  return PALETTE[idx] ?? FALLBACK_PALETTE;
}

function extractInitials(source: string): string {
  const cleaned = source.replace(/@.*$/, '').trim();
  if (cleaned.length === 0) return '?';
  const parts = cleaned.split(/[._\s-]+/).filter(Boolean);
  const first = parts[0];
  if (first === undefined) return cleaned.charAt(0).toUpperCase();
  const second = parts[1];
  if (second === undefined) return first.slice(0, 2).toUpperCase();
  return (first.charAt(0) + second.charAt(0)).toUpperCase();
}

export function AvatarInitials({
  source,
  size = 48,
  className,
  'data-testid': testId,
}: AvatarInitialsProps) {
  const initials = useMemo(() => extractInitials(source), [source]);
  const palette = useMemo(() => hashPalette(source), [source]);
  return (
    <span
      data-testid={testId ?? 'avatar-initials'}
      className={cn(
        'inline-grid place-items-center font-semibold uppercase tracking-wide',
        palette,
        className,
      )}
      style={{ width: size, height: size, fontSize: size * 0.4 }}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
}
